/**
 * 4D Timeliner → MP4 export.
 *
 * The browser owns the picture: it steps the viewer snapshot by snapshot,
 * composites the chosen overlays onto each captured still, and posts the frames
 * to the backend which stitches them with ffmpeg. That keeps the output
 * deterministic (same range → same video) and gives us burned-in overlays the
 * APS viewer could never draw itself.
 */
import { useState, useRef, useMemo, useEffect } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Film, Download, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BASES = [
  { value: 'PLAN', label: 'Plan (baseline dates)' },
  { value: 'FORECAST', label: 'Forecast (live start / finish)' },
  { value: 'ACTUAL', label: 'Actual (as-built)' },
  { value: 'PLAN_VS_ACTUAL', label: 'Plan vs Actual (variance colouring)' },
  { value: 'KPI', label: 'KPI health' },
  { value: 'CRITICAL_PATH', label: 'Critical path' },
];

const RESOLUTIONS = [
  { value: '1280x720', label: '720p · 1280 × 720' },
  { value: '1920x1080', label: '1080p · 1920 × 1080' },
  { value: '2560x1440', label: '1440p · 2560 × 1440' },
];

const LEGENDS = {
  PLAN: [['#3B82F6', 'Planned work in place'], ['#E0E0E0', 'Not yet planned']],
  FORECAST: [['#F59E0B', 'Forecast in place'], ['#EF4444', 'Forecast slipped > 7d'], ['#22C55E', 'Earlier than baseline']],
  ACTUAL: [['#10B981', 'Installed / complete'], ['#E0E0E0', 'Not installed']],
  PLAN_VS_ACTUAL: [['#10B981', 'Complete on time'], ['#22C55E', 'Ahead'], ['#EF4444', 'Behind'], ['#DC2626', 'Overdue']],
  KPI: [['#10B981', 'Completed'], ['#3B82F6', 'On track'], ['#F59E0B', 'Behind'], ['#EF4444', 'Critical delay']],
  CRITICAL_PATH: [['#DC2626', 'Critical path'], ['#10B981', 'CP complete'], ['#E0E0E0', 'Non-critical']],
};

const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export function TimelinerVideoExport({
  open, onOpenChange, projectId, modelId, modelName,
  mappings = [], initialBasis = 'PLAN_VS_ACTUAL', initialFrom = '', initialTo = '', viewerRef,
}) {
  const base = `${API}/api/v1/projects/${projectId}/4d/models/${modelId}`;

  const [source, setSource] = useState('COMMITTED');
  const [basis, setBasis] = useState(initialBasis);
  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);
  const [fps, setFps] = useState('4');
  const [resolution, setResolution] = useState('1920x1080');
  const [title, setTitle] = useState('');
  const [overlays, setOverlays] = useState({
    hud: true, legend: true, curve: true, activities: false, watermark: true,
  });

  const [range, setRange] = useState(null);           // {count, first, last} of the chosen basis
  const [phase, setPhase] = useState('idle');          // idle|preparing|capturing|encoding|ready|failed
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [job, setJob] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    if (open) { setBasis(initialBasis); setFromDate(initialFrom); setToDate(initialTo); }
  }, [open, initialBasis, initialFrom, initialTo]);

  // Preview how many steps the chosen basis + range will produce, so the user
  // sees "46 frames ≈ 12 s" before committing to a 90-second render.
  useEffect(() => {
    if (!open || source !== 'COMMITTED') { setRange(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${base}/timeliner/snapshots?mode=${basis}`);
        const d = await r.json();
        if (cancelled) return;
        const snaps = d.snapshots || [];
        setRange({ count: snaps.length,
                   first: dateOnly(snaps[0]?.snapshotDate),
                   last: dateOnly(snaps[snaps.length - 1]?.snapshotDate) });
      } catch { if (!cancelled) setRange(null); }
    })();
    return () => { cancelled = true; };
  }, [open, basis, source, base]);

  const [w, h] = resolution.split('x').map(Number);

  const selectedCount = useMemo(() => {
    if (!range) return null;
    return range.count;
  }, [range]);

  const reset = () => { setPhase('idle'); setDone(0); setTotal(0); setJob(null); cancelRef.current = false; };

  // ── Frame compositor ──────────────────────────────────────────────────
  const drawOverlays = (ctx, frame, meta) => {
    const { periodLabel, date, metrics, legend, curve, curveIndex, acts } = frame;
    const pad = Math.round(w * 0.018);
    ctx.save();

    if (overlays.hud) {
      const boxW = Math.round(w * 0.27);
      const boxH = Math.round(h * 0.17);
      ctx.fillStyle = 'rgba(8,12,22,0.72)';
      ctx.fillRect(pad, pad, boxW, boxH);
      ctx.strokeStyle = 'rgba(255,255,255,0.14)';
      ctx.strokeRect(pad, pad, boxW, boxH);

      ctx.fillStyle = '#F8FAFC';
      ctx.font = `600 ${Math.round(h * 0.030)}px Inter, system-ui, sans-serif`;
      ctx.fillText(periodLabel || '', pad + 16, pad + Math.round(h * 0.048));

      ctx.fillStyle = '#94A3B8';
      ctx.font = `${Math.round(h * 0.019)}px Inter, system-ui, sans-serif`;
      ctx.fillText(`${meta.basisLabel}  ·  ${date || ''}`, pad + 16, pad + Math.round(h * 0.078));

      const pp = metrics?.plannedProgressPct ?? 0;
      const ap = metrics?.actualProgressPct ?? 0;
      ctx.font = `600 ${Math.round(h * 0.021)}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = '#60A5FA';
      ctx.fillText(`Planned ${pp.toFixed(1)}%`, pad + 16, pad + Math.round(h * 0.113));
      ctx.fillStyle = '#34D399';
      ctx.fillText(`Actual ${ap.toFixed(1)}%`, pad + 16 + Math.round(boxW * 0.47), pad + Math.round(h * 0.113));
      ctx.fillStyle = '#CBD5E1';
      ctx.font = `${Math.round(h * 0.017)}px Inter, system-ui, sans-serif`;
      ctx.fillText(`${metrics?.objectsVisible ?? 0} objects in place  ·  ${metrics?.activitiesInProgress ?? 0} in progress`,
                   pad + 16, pad + Math.round(h * 0.148));
    }

    if (overlays.legend && legend?.length) {
      const lh = Math.round(h * 0.030);
      const boxH = legend.length * lh + Math.round(h * 0.022);
      const boxW = Math.round(w * 0.22);
      const x = pad;
      const y = h - pad - boxH - (overlays.curve ? Math.round(h * 0.17) : 0);
      ctx.fillStyle = 'rgba(8,12,22,0.80)';
      ctx.fillRect(x, y, boxW, boxH);
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.strokeRect(x, y, boxW, boxH);
      legend.forEach(([color, label], i) => {
        ctx.fillStyle = color;
        ctx.fillRect(x + 14, y + Math.round(h * 0.014) + i * lh, lh * 0.5, lh * 0.5);
        ctx.fillStyle = '#E2E8F0';
        ctx.font = `${Math.round(h * 0.016)}px Inter, system-ui, sans-serif`;
        ctx.fillText(label, x + 14 + lh * 0.8, y + Math.round(h * 0.014) + i * lh + lh * 0.45);
      });
    }

    if (overlays.curve && curve?.length > 1) {
      const cw = Math.round(w * 0.42);
      const ch = Math.round(h * 0.14);
      const x = pad;
      const y = h - pad - ch;
      ctx.fillStyle = 'rgba(8,12,22,0.82)';
      ctx.fillRect(x, y, cw, ch);
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.strokeRect(x, y, cw, ch);
      const px = (i) => x + 12 + (i / Math.max(1, curve.length - 1)) * (cw - 24);
      const py = (v) => y + ch - 12 - (v / 100) * (ch - 26);
      const line = (key, color) => {
        ctx.beginPath();
        curve.forEach((p, i) => (i ? ctx.lineTo(px(i), py(p[key])) : ctx.moveTo(px(i), py(p[key]))));
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(2, h * 0.0022);
        ctx.stroke();
      };
      line('planned', '#60A5FA');
      line('actual', '#34D399');
      const mx = px(curveIndex);
      ctx.beginPath(); ctx.moveTo(mx, y + 8); ctx.lineTo(mx, y + ch - 8);
      ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = Math.max(1.5, h * 0.0016); ctx.stroke();
      ctx.fillStyle = '#94A3B8';
      ctx.font = `${Math.round(h * 0.015)}px Inter, system-ui, sans-serif`;
      ctx.fillText('Progress S-curve — planned vs actual', x + 12, y + Math.round(h * 0.024));
    }

    if (overlays.activities && acts?.length) {
      const boxW = Math.round(w * 0.26);
      const rowH = Math.round(h * 0.026);
      const rows = acts.slice(0, 12);
      const boxH = rows.length * rowH + Math.round(h * 0.05);
      const x = w - pad - boxW;
      const y = pad;
      ctx.fillStyle = 'rgba(8,12,22,0.72)';
      ctx.fillRect(x, y, boxW, boxH);
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.strokeRect(x, y, boxW, boxH);
      ctx.fillStyle = '#F8FAFC';
      ctx.font = `600 ${Math.round(h * 0.018)}px Inter, system-ui, sans-serif`;
      ctx.fillText('This period', x + 14, y + Math.round(h * 0.032));
      rows.forEach((a, i) => {
        ctx.fillStyle = a.kind === 'finish' ? '#34D399' : '#60A5FA';
        ctx.fillRect(x + 14, y + Math.round(h * 0.046) + i * rowH, rowH * 0.28, rowH * 0.28);
        ctx.fillStyle = '#CBD5E1';
        ctx.font = `${Math.round(h * 0.0155)}px Inter, system-ui, sans-serif`;
        const label = `${a.code}  ${a.name}`;
        ctx.fillText(label.length > 44 ? `${label.slice(0, 44)}…` : label,
                     x + 14 + rowH * 0.5, y + Math.round(h * 0.050) + i * rowH + rowH * 0.28);
      });
    }

    if (overlays.watermark) {
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.font = `600 ${Math.round(h * 0.017)}px Inter, system-ui, sans-serif`;
      const txt = meta.title || `${modelName || '4D Model'} — Panorix 4D`;
      const tw = ctx.measureText(txt).width;
      ctx.fillText(txt, w - pad - tw, h - Math.round(h * 0.016));
    }
    ctx.restore();
  };

  // ── Export driver ─────────────────────────────────────────────────────
  const run = async () => {
    if (!viewerRef?.current?.isReady?.()) {
      toast.error('The 3D viewer is still loading — wait for the model then try again.');
      return;
    }
    cancelRef.current = false;
    setPhase('preparing');
    setDone(0);

    try {
      // 1. Resolve the frame list for the chosen source.
      let steps = [];
      let allSteps = [];                                   // full sequence — the S-curve reads off this
      let getObjects;   // (step, idx) → [{dbId, color, opacity}]

      if (source === 'COMMITTED') {
        const r = await authFetch(`${base}/timeliner/snapshots?mode=${basis}`);
        const d = await r.json();
        let snaps = d.snapshots || [];
        if (!snaps.length) {
          toast.error(`No ${basis} snapshots exist yet — run Generate Timeliner first.`);
          setPhase('idle'); return;
        }
        allSteps = snaps;
        if (fromDate) snaps = snaps.filter(s => dateOnly(s.snapshotDate) >= fromDate);
        if (toDate) snaps = snaps.filter(s => dateOnly(s.snapshotDate) <= toDate);
        steps = snaps;
        getObjects = async (s) => {
          const rr = await authFetch(`${base}/timeliner/snapshot/${s.sequenceIndex}?mode=${basis}`);
          const dd = await rr.json();
          return (dd.objectStates || [])
            .filter(o => o.state === 'VISIBLE' && o.dbId != null)
            .map(o => ({ dbId: o.dbId, color: o.colorHex, opacity: o.opacity ?? 0.95 }));
        };
      } else {
        const r = await authFetch(`${base}/cognara/timeliner/dry-run`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: basis, include: 'ACCEPTED_AND_PENDING' }),
        });
        const d = await r.json();
        if (!r.ok) { toast.error(d.detail || 'Dry run failed'); setPhase('idle'); return; }
        let st = d.steps || [];
        allSteps = st;
        if (fromDate) st = st.filter(s => dateOnly(s.snapshotDate) >= fromDate);
        if (toDate) st = st.filter(s => dateOnly(s.snapshotDate) <= toDate);
        steps = st;
        getObjects = async (s) => {
          const rr = await authFetch(`${base}/cognara/timeliner/dry-run/step/${s.sequenceIndex}`);
          const dd = await rr.json();
          return (dd.visibleObjects || []).map(o => ({ dbId: o.dbId, color: o.colorHex, opacity: 0.95 }));
        };
      }

      if (steps.length < 2) {
        toast.error('That date range covers fewer than 2 steps — widen it.');
        setPhase('idle'); return;
      }
      setTotal(steps.length);

      // 2. Open the job.
      const jr = await authFetch(`${base}/video/jobs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source, basis, fromDate: fromDate || null, toDate: toDate || null,
          fps: Number(fps), width: w, height: h, frameCount: steps.length,
          overlays, title: title || null,
        }),
      });
      const jd = await jr.json();
      if (!jr.ok) { toast.error(jd.detail || 'Could not start the export'); setPhase('idle'); return; }
      setJob(jd);
      setPhase('capturing');

      // 3. Pre-compute the S-curve and the per-period activity list. The curve
      //    spans the WHOLE programme even when the clip covers a slice of it —
      //    7 weeks of a 46-week job would otherwise draw as a flat line.
      const curve = allSteps.map(s => ({
        planned: s.metrics?.plannedProgressPct ?? 0,
        actual: s.metrics?.actualProgressPct ?? 0,
      }));
      const curveAt = (seqIndex) =>
        Math.max(0, allSteps.findIndex(s => s.sequenceIndex === seqIndex));
      const startKey = basis === 'ACTUAL' ? 'actualStart'
        : basis === 'FORECAST' ? 'forecastStart' : 'plannedStart';
      const finishKey = basis === 'ACTUAL' ? 'actualFinish'
        : basis === 'FORECAST' ? 'forecastFinish' : 'plannedFinish';
      const actsFor = (idx) => {
        if (!overlays.activities) return [];
        const to = dateOnly(steps[idx].snapshotDate);
        const from = idx > 0 ? dateOnly(steps[idx - 1].snapshotDate) : '0000-00-00';
        const hits = [];
        for (const m of mappings) {
          if (m.isSummary) continue;
          const sd = dateOnly(m[startKey]);
          const fd = dateOnly(m[finishKey]);
          if (fd && fd > from && fd <= to) hits.push({ kind: 'finish', code: m.activityCode, name: m.activityName });
          else if (sd && sd > from && sd <= to) hits.push({ kind: 'start', code: m.activityCode, name: m.activityName });
        }
        return hits;
      };

      // 4. Step → paint → capture → composite → upload.
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      const basisLabel = BASES.find(b => b.value === basis)?.label || basis;

      for (let i = 0; i < steps.length; i++) {
        if (cancelRef.current) { toast.info('Export cancelled'); setPhase('idle'); return; }
        const s = steps[i];
        const objs = await getObjects(s);
        viewerRef.current.paintFrame(objs, { ghostRest: true, isolate: false });
        await sleep(260);                                     // let the GPU settle
        const shot = await viewerRef.current.captureFrame(w, h);
        if (!shot) throw new Error('The viewer returned an empty frame.');

        const img = await new Promise((res, rej) => {
          const im = new Image();
          im.onload = () => res(im);
          im.onerror = () => rej(new Error('Frame decode failed'));
          im.src = shot;
        });
        ctx.fillStyle = '#0A0E1A';
        ctx.fillRect(0, 0, w, h);
        // cover-fit so the model never stretches
        const scale = Math.max(w / img.width, h / img.height);
        const dw = img.width * scale, dh = img.height * scale;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);

        drawOverlays(ctx, {
          periodLabel: s.periodLabel,
          date: dateOnly(s.snapshotDate),
          metrics: s.metrics,
          legend: LEGENDS[basis] || [],
          curve, curveIndex: curveAt(s.sequenceIndex),
          acts: actsFor(i),
        }, { basisLabel, title });

        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        const fr = await authFetch(`${base}/video/jobs/${jd.jobId}/frames/${i}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataUrl }),
        });
        if (!fr.ok) {
          const e = await fr.json().catch(() => ({}));
          throw new Error(e.detail || `Frame ${i + 1} upload failed`);
        }
        setDone(i + 1);
      }

      // 5. Encode and poll.
      setPhase('encoding');
      const er = await authFetch(`${base}/video/jobs/${jd.jobId}/encode`, { method: 'POST' });
      if (!er.ok) {
        const e = await er.json().catch(() => ({}));
        throw new Error(e.detail || 'Encoding could not be started');
      }
      for (let t = 0; t < 150; t++) {
        await sleep(2000);
        const sr = await authFetch(`${base}/video/jobs/${jd.jobId}`);
        const sd = await sr.json();
        setJob(sd);
        if (sd.status === 'READY') { setPhase('ready'); toast.success('Video ready'); return; }
        if (sd.status === 'FAILED') throw new Error(sd.error || 'Encoding failed');
      }
      throw new Error('Encoding timed out');
    } catch (e) {
      setPhase('failed');
      toast.error(e.message || 'Export failed');
    }
  };

  const download = async () => {
    try {
      const r = await authFetch(`${base}/video/jobs/${job.jobId}/download`);
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(modelName || '4D').replace(/[^A-Za-z0-9]+/g, '-')}-${basis}-${source}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || 'Download failed'); }
  };

  const busy = phase === 'preparing' || phase === 'capturing' || phase === 'encoding';
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="max-w-2xl" data-testid="video-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="w-4 h-4 text-orange-500" /> Export 4D timeliner video
          </DialogTitle>
          <DialogDescription>
            Renders the sequence frame by frame into an MP4 with the overlays burned in —
            ready to drop into a report or a progress review deck.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="space-y-1.5">
            <Label className="text-xs">Linkage source</Label>
            <Select value={source} onValueChange={setSource} disabled={busy}>
              <SelectTrigger className="h-8" data-testid="video-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="COMMITTED">Committed 4D links</SelectItem>
                <SelectItem value="DRY_RUN">Cognara dry run (proposed links)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Date basis</Label>
            <Select value={basis} onValueChange={setBasis} disabled={busy}>
              <SelectTrigger className="h-8" data-testid="video-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BASES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                   className="h-8" disabled={busy} data-testid="video-from" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                   className="h-8" disabled={busy} data-testid="video-to" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Frames per second</Label>
            <Select value={fps} onValueChange={setFps} disabled={busy}>
              <SelectTrigger className="h-8" data-testid="video-fps"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['1', '2', '4', '6', '8', '12'].map(f =>
                  <SelectItem key={f} value={f}>{f} fps</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Resolution</Label>
            <Select value={resolution} onValueChange={setResolution} disabled={busy}>
              <SelectTrigger className="h-8" data-testid="video-resolution"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RESOLUTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Title burned into the corner (optional)</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} disabled={busy}
                   placeholder={`${modelName || '4D Model'} — Panorix 4D`}
                   className="h-8" data-testid="video-title" />
          </div>
        </div>

        <div className="border-t pt-3">
          <Label className="text-xs mb-2 block">Overlays to burn in</Label>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            {[
              ['hud', 'Date / week + planned vs actual %'],
              ['legend', 'Colour legend'],
              ['curve', 'Progress S-curve with a moving marker'],
              ['activities', 'Activities starting / finishing this period'],
              ['watermark', 'Model name or custom title'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={overlays[key]} disabled={busy}
                          onCheckedChange={v => setOverlays(o => ({ ...o, [key]: !!v }))}
                          data-testid={`video-overlay-${key}`} />
                {label}
              </label>
            ))}
          </div>
        </div>

        {source === 'COMMITTED' && range && (
          <p className="text-[11px] text-muted-foreground" data-testid="video-range-hint">
            {basis} has <strong>{range.count}</strong> snapshots spanning {range.first} → {range.last}.
            {selectedCount ? ` At ${fps} fps the full sequence runs about ${(range.count / Number(fps)).toFixed(0)}s.` : ''}
            {' '}Leave the dates blank for the whole sequence.
          </p>
        )}

        {busy && (
          <div className="space-y-1.5" data-testid="video-progress">
            <Progress value={phase === 'encoding' ? 100 : pct} />
            <p className="text-[11px] text-muted-foreground">
              {phase === 'preparing' && 'Resolving the snapshot sequence…'}
              {phase === 'capturing' && `Capturing frame ${done} of ${total} — keep this tab in the foreground.`}
              {phase === 'encoding' && 'Stitching the MP4 on the server…'}
            </p>
          </div>
        )}

        {phase === 'ready' && job && (
          <div className="flex items-center gap-2 text-xs" data-testid="video-ready">
            <Badge className="bg-emerald-600">Ready</Badge>
            <span className="text-muted-foreground">
              {total} frames · {(job.sizeBytes / 1048576).toFixed(1)} MB · {(total / Number(fps)).toFixed(0)}s
            </span>
          </div>
        )}
        {phase === 'failed' && (
          <p className="text-xs text-red-500" data-testid="video-failed">
            {job?.error || 'The export failed. Try a smaller resolution or a narrower range.'}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          {busy && (
            <Button variant="outline" size="sm" onClick={() => { cancelRef.current = true; }}
                    data-testid="video-cancel">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          )}
          {phase === 'ready' ? (
            <>
              <Button variant="outline" size="sm" onClick={reset} data-testid="video-another">
                Export another
              </Button>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={download}
                      data-testid="video-download">
                <Download className="w-3 h-3 mr-1" /> Download MP4
              </Button>
            </>
          ) : (
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={run} disabled={busy}
                    data-testid="video-export-start">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Film className="w-3 h-3 mr-1" />}
              Render video
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TimelinerVideoExport;
